import {
  BooleanInput,
  CreateButton,
  Datagrid,
  DateInput,
  EditButton,
  FunctionField,
  List,
  NumberInput,
  TextField,
  TextInput,
} from 'react-admin';
import Link from '@mui/material/Link';

import { DataTableListActions } from 'src/client/components/data-table-list-actions';
import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { includesInsensitive } from 'src/client/components/forms/form-utils';
import { CampaignArchiveButton } from './CampaignArchiveButton';

type RelatedRecord = {
  name?: string | null;
};

type CampaignRecord = {
  advertiser?: RelatedRecord | null;
  advertiserByAdvertiserId?: RelatedRecord | null;
  advertiserId?: string | null;
  campaignNumber?: number | null;
  archivedAt?: string | null;
  name?: string | null;
  sharepointContractUrl?: string | null;
};

export const getRelatedName = (
  record: Record<string, any> | undefined,
  relationKeys: string[],
  fallbackKey?: string,
) => {
  if (!record) return '';

  for (const key of relationKeys) {
    const value = record[key];
    if (value?.name) return value.name;
  }

  return fallbackKey ? record[fallbackKey] ?? '' : '';
};

export const formatCampaignTitle = (record?: CampaignRecord) => {
  if (!record) return '';

  const number = record.campaignNumber ? `#${record.campaignNumber}` : 'Unnumbered';
  return record.name ? `${number} ${record.name}` : number;
};

export const SharePointUrlField = ({ source = 'sharepointContractUrl' }: { source?: string }) => (
  <FunctionField
    source={source}
    label="SharePoint URL"
    render={(record: Record<string, any>) => {
      const value = record?.[source];
      if (!value) return null;

      return (
        <Link href={value} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>
          Open contract
        </Link>
      );
    }}
  />
);

const dateOnOrAfterFilter = {
  parse: (value?: string) =>
    value
      ? {
          operator: 'greaterThanOrEqualTo',
          value,
        }
      : undefined,
  format: (value?: string | { value?: string }) => (typeof value === 'string' ? value : value?.value ?? ''),
};

const campaignNameFilter = {
  parse: (value?: string) => {
    const trimmedValue = value?.trim();

    return trimmedValue ? includesInsensitive.parse(trimmedValue) : undefined;
  },
  format: (value?: string | { value?: string | null }) => (typeof value === 'string' ? value : value?.value ?? ''),
};

export const campaignFilters = [
  <TextInput key="name" label="Campaign Name" source="name" {...campaignNameFilter} alwaysOn resettable />,
  <NumberInput key="campaignNumber" label="Campaign Number" source="campaignNumber" alwaysOn />,
  <BooleanInput key="includeArchived" label="Include Archived" source="includeArchived" alwaysOn />,
  <DateInput key="startDate" label="Starting On / After" source="startDate" {...dateOnOrAfterFilter} />,
];

const CampaignListActions = () => (
  <DataTableListActions filters={campaignFilters}>
    <CreateButton />
  </DataTableListActions>
);

export const CampaignList = () => (
  <List
    actions={<CampaignListActions />}
    sort={{ field: 'startDate', order: 'DESC' }}
    perPage={25}
    filterDefaultValues={{ includeArchived: false }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <FunctionField
        source="advertiserId"
        label="Advertiser"
        render={record => getRelatedName(record, ['advertiser', 'advertiserByAdvertiserId'], 'advertiserId')}
      />
      <FunctionField source="name" label="Campaign" render={(record: CampaignRecord) => formatCampaignTitle(record)} />
      <ESTDateField source="startDate" label="Start Date" textAlign="center" />
      <ESTDateField source="endDate" label="End Date" textAlign="center" />
      <TextField source="gamOrderId" label="GAM Order ID" />
      <TextField source="dcmAdvertiserId" label="DCM Advertiser ID" />
      <FunctionField
        source="archivedAt"
        label="Status"
        render={(record: CampaignRecord) => (record.archivedAt ? 'Archived' : 'Active')}
      />
      <SharePointUrlField />
      <CampaignArchiveButton />
      <EditButton />
    </Datagrid>
  </List>
);
